import React, { useState, useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { 
  ExternalLink, 
  Users, 
  Rocket, 
  UserPlus, 
  Users2, 
  Crown, 
  MoreHorizontal,
  LogOut,
  Trash2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ChallengeModal from "@/components/challenges/ChallengeModal";
import ChallengeJoinFlow from "@/components/challenges/ChallengeJoinFlow";
import InviteTeamMemberDialog from "@/components/challenges/InviteTeamMemberDialog";
import StartTeamInviteFlow from "@/components/challenges/StartTeamInviteFlow";
import SwitchTeamDialog from "@/components/challenges/SwitchTeamDialog";
import DeleteTeamDialog from "@/components/challenges/DeleteTeamDialog";
import TransferLeadershipDialog from "@/components/challenges/TransferLeadershipDialog";
import { RecommendedChallengesCarousel } from "@/components/challenges/RecommendedChallengesCarousel";
import EventChallengesBadge from "@/components/dashboard/EventChallengesBadge";

interface Challenge {
  id: string;
  title: string;
  one_liner: string;
  status: string;
  difficulty: string;
  tags: string[];
  claimed_by: string | null;
  event_id: string | null;
}

interface TeamInfo {
  id: string;
  name: string;
}

interface TeamMembership {
  challenge_id: string;
  role: string;
  team_id: string | null;
  challenges: Challenge;
  challenge_teams: TeamInfo | null;
}

interface EventInfo {
  id: string;
  name: string;
  event_date: string;
}

// Unified challenge entry combining team membership, claimed, and followed states
interface UnifiedChallengeEntry {
  challenge_id: string;
  role: string | null;
  team_id: string | null;
  challenges: Challenge;
  challenge_teams: TeamInfo | null;
  isTeamMember: boolean;
  isClaimant: boolean;
  isFollowing: boolean;
}

const UserChallenges: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedChallengeId, setSelectedChallengeId] = useState<string | null>(null);
  const [selectedChallengeEditMode, setSelectedChallengeEditMode] = useState(false);
  
  // Invite dialogs state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteDialogTeam, setInviteDialogTeam] = useState<{teamId: string, teamName: string, challengeId: string, isLead: boolean} | null>(null);
  const [startTeamInviteOpen, setStartTeamInviteOpen] = useState(false);
  const [startTeamInviteChallenge, setStartTeamInviteChallenge] = useState<{id: string, title: string} | null>(null);
  const [switchTeamOpen, setSwitchTeamOpen] = useState(false);
  const [switchTeamData, setSwitchTeamData] = useState<{challengeId: string, currentTeamId: string, isLead: boolean} | null>(null);
  const [challengeJoinFlowOpen, setChallengeJoinFlowOpen] = useState(false);
  const [challengeJoinFlowData, setChallengeJoinFlowData] = useState<{
    id: string;
    title: string;
    eventId: string | null;
  } | null>(null);
  
  // Team management dialogs state
  const [deleteTeamOpen, setDeleteTeamOpen] = useState(false);
  const [deleteTeamData, setDeleteTeamData] = useState<{teamId: string, teamName: string, challengeId: string, memberCount: number} | null>(null);
  const [transferLeadershipOpen, setTransferLeadershipOpen] = useState(false);
  const [leaveTeamOpen, setLeaveTeamOpen] = useState(false);
  const [transferData, setTransferData] = useState<{teamId: string, teamName: string, challengeId: string} | null>(null);
  const [teamMembers, setTeamMembers] = useState<Array<{id: string, user_id: string, email: string, role: string}>>([]);
  const [currentUserName, setCurrentUserName] = useState("Team Lead");

  // Fetch current user's display name
  useEffect(() => {
    const fetchUserName = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data: profile } = await supabase.from("contributor_profiles").select("display_name").eq("id", user.id).single();
      if (profile?.display_name) setCurrentUserName(profile.display_name);
    };
    fetchUserName();
  }, []);

  // Fetch team members for dialogs
  const fetchTeamMembers = async (teamId: string) => {
    const { data: membersData } = await supabase.from("challenge_team_members").select("id, user_id, role").eq("team_id", teamId);
    const userIds = membersData?.map((m) => m.user_id) || [];
    const { data: profiles } = await supabase.from("profiles").select("id, email").in("id", userIds);
    const profileMap = new Map(profiles?.map((p) => [p.id, p.email || ""]));
    return (membersData || []).map((m) => ({ id: m.id, user_id: m.user_id, email: profileMap.get(m.user_id) || "Unknown", role: m.role || "member" }));
  };

  // Fetch user challenge memberships with event info (including claimed + followed)
  const { 
    data: membershipsData, 
    isLoading: loading,
  } = useQuery({
    queryKey: ["user-challenge-memberships-with-events"],
    staleTime: 30000,
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/login?redirect=/dashboard/challenges");
        return { memberships: [], userId: null, events: {}, teamMemberCounts: {} };
      }

      // 1. Fetch team memberships
      const { data: teamData, error: teamError } = await supabase
        .from("challenge_team_members")
        .select(`
          challenge_id,
          role,
          team_id,
          challenges (
            id,
            title,
            one_liner,
            status,
            difficulty,
            tags,
            claimed_by,
            event_id
          ),
          challenge_teams (
            id,
            name,
            deleted_at
          )
        `)
        .eq("user_id", user.id);

      if (teamError) {
        console.error("Error fetching team memberships:", teamError);
        toast.error("Failed to load your challenges");
        throw teamError;
      }
      
      // 2. Fetch challenges user claimed
      const { data: claimedData } = await supabase
        .from("challenges")
        .select("id, title, one_liner, status, difficulty, tags, claimed_by, event_id")
        .eq("claimed_by", user.id);

      // 3. Fetch challenges user follows
      const { data: followsData } = await supabase
        .from("user_follows")
        .select("entity_id")
        .eq("user_id", user.id)
        .eq("entity_type", "challenge");

      const followedChallengeIds = new Set(followsData?.map(f => f.entity_id) || []);

      // 4. Fetch full challenge data for followed challenges not already in team/claimed
      const teamChallengeIds = new Set((teamData || []).map((m: any) => m.challenge_id));
      const claimedChallengeIds = new Set((claimedData || []).map(c => c.id));
      
      const missingFollowedIds = [...followedChallengeIds].filter(
        id => !teamChallengeIds.has(id) && !claimedChallengeIds.has(id)
      );

      let followedChallengesData: Challenge[] = [];
      if (missingFollowedIds.length > 0) {
        const { data: followedData } = await supabase
          .from("challenges")
          .select("id, title, one_liner, status, difficulty, tags, claimed_by, event_id")
          .in("id", missingFollowedIds);
        followedChallengesData = followedData || [];
      }

      // 5. Merge into unified structure, deduplicating by challenge_id
      const unifiedMap = new Map<string, UnifiedChallengeEntry>();

      // Add team memberships (filter out soft-deleted teams)
      (teamData || [])
        .filter((m: any) => !m.challenge_teams?.deleted_at)
        .forEach((m: any) => {
          unifiedMap.set(m.challenge_id, {
            challenge_id: m.challenge_id,
            role: m.role,
            team_id: m.team_id,
            challenges: m.challenges,
            challenge_teams: m.challenge_teams,
            isTeamMember: true,
            isClaimant: m.challenges.claimed_by === user.id,
            isFollowing: followedChallengeIds.has(m.challenge_id),
          });
        });

      // Add claimed challenges not already in map
      (claimedData || []).forEach((c) => {
        if (!unifiedMap.has(c.id)) {
          unifiedMap.set(c.id, {
            challenge_id: c.id,
            role: null,
            team_id: null,
            challenges: c,
            challenge_teams: null,
            isTeamMember: false,
            isClaimant: true,
            isFollowing: followedChallengeIds.has(c.id),
          });
        }
      });

      // Add followed challenges not already in map
      followedChallengesData.forEach((c) => {
        if (!unifiedMap.has(c.id)) {
          unifiedMap.set(c.id, {
            challenge_id: c.id,
            role: null,
            team_id: null,
            challenges: c,
            challenge_teams: null,
            isTeamMember: false,
            isClaimant: false,
            isFollowing: true,
          });
        }
      });

      const unifiedMemberships = Array.from(unifiedMap.values());

      // Get unique event IDs
      const eventIds = [...new Set(
        unifiedMemberships
          .map(m => m.challenges.event_id)
          .filter((id): id is string => !!id)
      )];

      // Fetch event info
      let eventsMap: Record<string, EventInfo> = {};
      if (eventIds.length > 0) {
        const { data: eventsData } = await supabase
          .from("events")
          .select("id, name, event_date")
          .in("id", eventIds);
        
        eventsData?.forEach(e => {
          eventsMap[e.id] = e;
        });
      }

      // Fetch team member counts
      const teamIds = unifiedMemberships
        .map(m => m.team_id)
        .filter((id): id is string => !!id);
      
      let teamMemberCounts: Record<string, number> = {};
      if (teamIds.length > 0) {
        const { data: membersData } = await supabase
          .from("challenge_team_members")
          .select("team_id")
          .in("team_id", teamIds);
        
        membersData?.forEach(m => {
          teamMemberCounts[m.team_id] = (teamMemberCounts[m.team_id] || 0) + 1;
        });
      }
      
      return { 
        memberships: unifiedMemberships, 
        userId: user.id,
        events: eventsMap,
        teamMemberCounts
      };
    },
  });

  // Extract values from query result
  const memberships = membershipsData?.memberships ?? [];
  const currentUserId = membershipsData?.userId ?? null;
  const events = membershipsData?.events ?? {};
  const teamMemberCounts = membershipsData?.teamMemberCounts ?? {};

  // Sort challenges by event date (upcoming first), then non-event challenges
  const sortedMemberships = useMemo(() => {
    return [...memberships].sort((a, b) => {
      const eventA = a.challenges.event_id ? events[a.challenges.event_id] : null;
      const eventB = b.challenges.event_id ? events[b.challenges.event_id] : null;
      
      // If both have events, sort by event date
      if (eventA && eventB) {
        return new Date(eventA.event_date).getTime() - new Date(eventB.event_date).getTime();
      }
      // Event challenges come before non-event challenges
      if (eventA && !eventB) return -1;
      if (!eventA && eventB) return 1;
      return 0;
    });
  }, [memberships, events]);

  // Invalidate challenges cache
  const invalidateChallenges = () => {
    queryClient.invalidateQueries({ queryKey: ["user-challenge-memberships-with-events"] });
    queryClient.invalidateQueries({ queryKey: ["dashboard-nav-counts"] });
  };

  // Determine primary CTA for a challenge
  const getPrimaryCTA = (membership: UnifiedChallengeEntry) => {
    const { team_id, role, challenges, challenge_teams } = membership;
    const memberCount = team_id ? (teamMemberCounts[team_id] || 1) : 0;

    if (!team_id) {
      // No team - create or join
      return {
        label: "Create or Join Team",
        icon: Users2,
        onClick: () => {
          setChallengeJoinFlowData({
            id: challenges.id,
            title: challenges.title,
            eventId: challenges.event_id
          });
          setChallengeJoinFlowOpen(true);
        }
      };
    }

    if (memberCount < 3) {
      // Small team - invite teammates
      return {
        label: "Invite Teammates",
        icon: UserPlus,
        onClick: () => {
          setInviteDialogTeam({
            teamId: team_id,
            teamName: challenge_teams?.name || "Your Team",
            challengeId: challenges.id,
            isLead: role === "lead"
          });
          setInviteDialogOpen(true);
        }
      };
    }

    // Full team - view details
    return {
      label: "View Progress",
      icon: ExternalLink,
      onClick: () => {
        setSelectedChallengeId(challenges.id);
        setSelectedChallengeEditMode(false);
      }
    };
  };

  const renderChallengeCard = (membership: UnifiedChallengeEntry) => {
    const { challenge_id, role, team_id, challenges: challenge, challenge_teams, isTeamMember, isClaimant: userIsClaimant, isFollowing } = membership;
    const primaryCTA = getPrimaryCTA(membership);
    const memberCount = team_id ? (teamMemberCounts[team_id] || 1) : 0;

    return (
      <Card 
        key={challenge_id} 
        className={cn(
          "hover:border-primary/50 transition-colors",
          userIsClaimant && "ring-2 ring-amber-500/30"
        )}
      >
        <CardContent className="pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {/* Event Badge */}
              {challenge.event_id && (
                <div className="mb-2">
                  <EventChallengesBadge eventId={challenge.event_id} />
                </div>
              )}
              
              {/* Title */}
              <h3 
                className="font-semibold text-foreground mb-1 cursor-pointer hover:text-primary transition-colors"
                onClick={() => setSelectedChallengeId(challenge.id)}
              >
                {challenge.title}
              </h3>
              
              {/* One-liner */}
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {challenge.one_liner}
              </p>

              {/* Team info */}
              {team_id && challenge_teams && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Users className="h-4 w-4" />
                  <span>{challenge_teams.name}</span>
                  <span className="text-xs">({memberCount} {memberCount === 1 ? 'member' : 'members'})</span>
                  {role === "lead" && (
                    <Badge variant="outline" className="text-xs">Lead</Badge>
                  )}
                </div>
              )}

              {/* Status badges */}
              <div className="flex items-center gap-2 flex-wrap">
                {userIsClaimant && (
                  <Badge variant="secondary" className="gap-1 text-xs border-primary/30 bg-primary/10 text-primary">
                    <Crown className="h-3 w-3" />
                    You claimed this
                  </Badge>
                )}
                {isFollowing && !isTeamMember && (
                  <Badge variant="outline" className="text-xs">
                    Following
                  </Badge>
                )}
              </div>
            </div>

            {/* Overflow menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSelectedChallengeId(challenge.id)}>
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                {userIsClaimant && (
                  <DropdownMenuItem onClick={() => {
                    setSelectedChallengeId(challenge.id);
                    setSelectedChallengeEditMode(true);
                  }}>
                    Edit Challenge
                  </DropdownMenuItem>
                )}
                {team_id && (
                  <DropdownMenuItem onClick={() => {
                    setSwitchTeamData({
                      challengeId: challenge.id,
                      currentTeamId: team_id,
                      isLead: role === "lead"
                    });
                    setSwitchTeamOpen(true);
                  }}>
                    Switch Team
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => {
                  setStartTeamInviteChallenge({ id: challenge.id, title: challenge.title });
                  setStartTeamInviteOpen(true);
                }}>
                  <Users2 className="h-4 w-4 mr-2" />
                  Invite to New Team
                </DropdownMenuItem>
                
                {/* Team lead management options */}
                {role === "lead" && team_id && challenge_teams && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={async () => {
                      const members = await fetchTeamMembers(team_id);
                      setTeamMembers(members);
                      setTransferData({
                        teamId: team_id,
                        teamName: challenge_teams.name,
                        challengeId: challenge.id
                      });
                      setTransferLeadershipOpen(true);
                    }}>
                      <Crown className="h-4 w-4 mr-2" />
                      Transfer Leadership
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={async () => {
                      const members = await fetchTeamMembers(team_id);
                      setTeamMembers(members);
                      setTransferData({
                        teamId: team_id,
                        teamName: challenge_teams.name,
                        challengeId: challenge.id
                      });
                      setLeaveTeamOpen(true);
                    }}>
                      <LogOut className="h-4 w-4 mr-2" />
                      Leave Team
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => {
                        setDeleteTeamData({
                          teamId: team_id,
                          teamName: challenge_teams.name,
                          challengeId: challenge.id,
                          memberCount: memberCount
                        });
                        setDeleteTeamOpen(true);
                      }}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Team
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Primary CTA */}
          <div className="mt-4">
            <Button 
              onClick={primaryCTA.onClick}
              className="w-full sm:w-auto gap-2"
            >
              <primaryCTA.icon className="h-4 w-4" />
              {primaryCTA.label}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <Helmet>
          <title>My Challenges | Dashboard</title>
        </Helmet>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-foreground">My Challenges</h1>
          <p className="text-muted-foreground">Challenges you're working on</p>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <Helmet>
        <title>My Challenges | Dashboard</title>
      </Helmet>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">My Challenges</h1>
        <p className="text-muted-foreground">
          Challenges you're working on with your team
        </p>
      </div>

      {memberships.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Rocket className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Active Challenges</h3>
            <p className="text-muted-foreground mb-4">
              You haven't joined any challenges yet. Check out the recommendations below!
            </p>
            <Button onClick={() => navigate("/challenges")}>
              Browse All Challenges
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {sortedMemberships.map(renderChallengeCard)}
        </div>
      )}

      {/* Recommendations Carousel */}
      <Separator className="my-8" />
      <RecommendedChallengesCarousel onViewMore={() => navigate("/challenges")} />

      {/* Modals */}
      {selectedChallengeId && (
        <ChallengeModal
          challengeId={selectedChallengeId}
          open={!!selectedChallengeId}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedChallengeId(null);
              setSelectedChallengeEditMode(false);
            }
          }}
          onUpdate={invalidateChallenges}
          initialEditMode={selectedChallengeEditMode}
        />
      )}

      {inviteDialogTeam && (
        <InviteTeamMemberDialog
          open={inviteDialogOpen}
          onOpenChange={(open) => {
            setInviteDialogOpen(open);
            if (!open) setInviteDialogTeam(null);
          }}
          teamId={inviteDialogTeam.teamId}
          teamName={inviteDialogTeam.teamName}
          challengeId={inviteDialogTeam.challengeId}
          isTeamLead={inviteDialogTeam.isLead}
          onInviteSent={invalidateChallenges}
        />
      )}

      {startTeamInviteChallenge && (
        <StartTeamInviteFlow
          open={startTeamInviteOpen}
          onOpenChange={(open) => {
            setStartTeamInviteOpen(open);
            if (!open) setStartTeamInviteChallenge(null);
          }}
          challengeId={startTeamInviteChallenge.id}
          challengeTitle={startTeamInviteChallenge.title}
          onSuccess={invalidateChallenges}
        />
      )}

      {switchTeamData && (
        <SwitchTeamDialog
          open={switchTeamOpen}
          onOpenChange={(open) => {
            setSwitchTeamOpen(open);
            if (!open) setSwitchTeamData(null);
          }}
          challengeId={switchTeamData.challengeId}
          currentTeamId={switchTeamData.currentTeamId}
          currentUserIsLead={switchTeamData.isLead}
          onTeamSwitched={invalidateChallenges}
        />
      )}

      {/* Team Management Dialogs */}
      {deleteTeamData && (
        <DeleteTeamDialog
          open={deleteTeamOpen}
          onOpenChange={(open) => {
            setDeleteTeamOpen(open);
            if (!open) setDeleteTeamData(null);
          }}
          teamId={deleteTeamData.teamId}
          teamName={deleteTeamData.teamName}
          memberCount={deleteTeamData.memberCount}
          challengeId={deleteTeamData.challengeId}
          leaderName={currentUserName}
          onTeamDeleted={invalidateChallenges}
        />
      )}

      {transferData && currentUserId && (
        <TransferLeadershipDialog
          open={transferLeadershipOpen}
          onOpenChange={(open) => {
            setTransferLeadershipOpen(open);
            if (!open) setTransferData(null);
          }}
          teamId={transferData.teamId}
          teamName={transferData.teamName}
          members={teamMembers}
          currentUserId={currentUserId}
          onTransferComplete={invalidateChallenges}
          leaveAfterTransfer={false}
        />
      )}

      {transferData && currentUserId && (
        <TransferLeadershipDialog
          open={leaveTeamOpen}
          onOpenChange={(open) => {
            setLeaveTeamOpen(open);
            if (!open) setTransferData(null);
          }}
          teamId={transferData.teamId}
          teamName={transferData.teamName}
          members={teamMembers}
          currentUserId={currentUserId}
          onTransferComplete={invalidateChallenges}
          leaveAfterTransfer={true}
        />
      )}

      {/* Challenge Join Flow for Create or Join Team */}
      {challengeJoinFlowData && (
        <ChallengeJoinFlow
          open={challengeJoinFlowOpen}
          onOpenChange={(open) => {
            setChallengeJoinFlowOpen(open);
            if (!open) {
              setChallengeJoinFlowData(null);
              invalidateChallenges();
            }
          }}
          challengeId={challengeJoinFlowData.id}
          eventId={challengeJoinFlowData.eventId}
          challengeTitle={challengeJoinFlowData.title}
          initialAction="join"
          startAtStep={2}
        />
      )}
    </div>
  );
};

export default UserChallenges;
